import {
  AllCommunityModule,
  ColDef,
  ColGroupDef,
  FilterChangedEvent,
  GetRowIdFunc,
  GridReadyEvent,
  IDatasource,
  InfiniteRowModelModule,
  ModuleRegistry,
  RowClickedEvent,
  SortChangedEvent,
  themeQuartz,
  ThemeDefaultParams,
  PaginationChangedEvent,
} from "ag-grid-community";
import { AgGridReact } from "ag-grid-react";
import { forwardRef, ReactNode, Ref, useMemo } from "react";
import {
  DEFAULT_CACHE_BLOCK_SIZE,
  DEFAULT_PAGE_SIZE,
  DEFAULT_PAGE_SIZE_SELECTOR,
} from "@/constants";
// Uncomment when the i18N is properly set up and translation text is ready
// import { useTranslation } from "react-i18next";
// import { getAgGridLocaleText } from "./AgGridLocaleText";
import CostcoBlueLoader from "../LoadingIndicator";

type DefaultGridProps<T> = {
  rowId: GetRowIdFunc<T>;
  datasource?: IDatasource;
  columnDefs: (ColDef<T> | ColGroupDef<T>)[];
  rowData?: T[];
  pinnedTopRowData?: T[]; // For pinning rows at the top
  loading?: boolean;
  onSortChanged?: (event: SortChangedEvent<T>) => void;
  onFilterChanged?: (event: FilterChangedEvent<T>) => void;
  onRowClicked?: (event: RowClickedEvent<T>) => void;
  onGridReady?: (event: GridReadyEvent<T>) => void;
  onPaginationPageSizeChanged?: (event: PaginationChangedEvent) => void;
  noRowsFoundText?: string;
  quickFilterText?: string;
  multiSortKey?: "ctrl" | "shift";
  domLayout?: "autoHeight" | "normal";
  getRowStyle?: (params: any) => any;
};

ModuleRegistry.registerModules([InfiniteRowModelModule, AllCommunityModule]);
const gridTheme: Partial<ThemeDefaultParams> = {
  fontSize: 14,
  spacing: 10,
  rowHeight: 42,
  oddRowBackgroundColor: "#FAFAFB",
  headerRowBorder: { width: 2 },
  headerFontWeight: 600,
  wrapperBorder: false,
  rowBorder: { style: "solid" },
  rowHoverColor: "rgba(245,245,245,0.5)",
  columnHoverColor: "rgba(238,238,238,0.4)",
  columnBorder: { style: "solid" },
};
const defsStyle: Partial<ColDef> = {
  headerClass: "defaultTableHeader",
  flex: 1,
  filterParams: { maxNumConditions: 1 },
  resizable: true, // Enable resizing
  autoHeight: true, // Adjust Cell Height to Fit Wrapped Text
  autoHeaderHeight: true, // Adjust Header Height to Fit Wrapped Text}
};

const DefaultGridInner = <T,>(
  {
    columnDefs,
    rowData,
    datasource,
    pinnedTopRowData,
    loading,
    onSortChanged,
    onFilterChanged,
    onRowClicked,
    onPaginationPageSizeChanged,
    rowId,
    noRowsFoundText,
    onGridReady,
    quickFilterText,
    multiSortKey,
    getRowStyle,
    domLayout = "autoHeight",
  }: DefaultGridProps<T>,
  ref: Ref<AgGridReact<T>>,
): ReactNode => {
  // const { t } = useTranslation();
  const loadingOverlay = () => <CostcoBlueLoader />;
  const modelType = datasource ? "infinite" : "clientSide";
  const quickFilter =
    !datasource && quickFilterText ? quickFilterText : undefined;
  const defaultColDef = useMemo<ColDef>(() => {
    return defsStyle;
  }, []);

  return (
    <AgGridReact<T>
      getRowId={rowId}
      rowModelType={modelType}
      rowData={rowData}
      datasource={datasource ?? undefined}
      domLayout={domLayout} // Changed from 'autoHeight' to 'normal' to fill parent container and enable scrolling
      animateRows={false}
      columnDefs={columnDefs}
      cacheBlockSize={datasource ? DEFAULT_CACHE_BLOCK_SIZE : undefined}
      infiniteInitialRowCount={DEFAULT_PAGE_SIZE}
      defaultCsvExportParams={{}}
      defaultColDef={defaultColDef}
      loading={loading}
      // localeText={getAgGridLocaleText(t)}
      pagination
      paginationPageSize={DEFAULT_PAGE_SIZE}
      paginationPageSizeSelector={DEFAULT_PAGE_SIZE_SELECTOR}
      onPaginationChanged={onPaginationPageSizeChanged}
      valueCache
      // rowSelection={{ mode: "singleRow", checkboxes: false }}
      multiSortKey={multiSortKey === "ctrl" ? "ctrl" : undefined}
      suppressMultiSort={!multiSortKey}
      onSortChanged={onSortChanged}
      onFilterChanged={onFilterChanged}
      onRowClicked={onRowClicked}
      columnHoverHighlight
      quickFilterText={quickFilter}
      tooltipMouseTrack
      rowClass={onRowClicked ? "cursor-pointer" : ""}
      // className="w-full h-full" // Changed from h-96 to fill parent
      className="h-96" // Changed from h-96 to fill parent
      theme={themeQuartz.withParams(gridTheme)}
      loadingOverlayComponent={loadingOverlay}
      overlayNoRowsTemplate={noRowsFoundText}
      onGridReady={onGridReady}
      pinnedTopRowData={pinnedTopRowData}
      getRowStyle={getRowStyle}
      ref={ref} // Forward the ref here
    />
  );
};

// Wrap the component with forwardRef
const DefaultGrid = forwardRef(DefaultGridInner) as <T>(
  props: DefaultGridProps<T> & { ref?: Ref<AgGridReact<T>> },
) => ReactNode;

export default DefaultGrid;
